import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { linksAPI, tokenAPI } from '@/lib/api';
import OBCLanDiscovery from '@/lib/obcLanDiscovery';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Link2, ExternalLink, Key, Info, Plus, Trash2, Wifi } from 'lucide-react';

export default function MyLinksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRedeem, setShowRedeem] = useState(false);
  const [tokenCode, setTokenCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [deleteLink, setDeleteLink] = useState(null);
  const [lanStatuses, setLanStatuses] = useState({});

  useEffect(() => { loadData(); }, []);

  // Subscribe to LAN discovery changes
  useEffect(() => {
    const unsubscribe = OBCLanDiscovery.subscribe(setLanStatuses);
    return unsubscribe;
  }, []);

  // Run LAN discovery when links load
  useEffect(() => {
    if (links.length > 0) {
      OBCLanDiscovery.checkAll(links);
    }
  }, [links]);

  // Gestione token da URL o sessionStorage
  useEffect(() => {
    // Check URL query param
    const redeemToken = searchParams.get('redeem');
    if (redeemToken) {
      setTokenCode(redeemToken);
      setShowRedeem(true);
      // Rimuovi il parametro dalla URL
      searchParams.delete('redeem');
      setSearchParams(searchParams, { replace: true });
      return;
    }

    // Check sessionStorage (da login redirect)
    const pendingToken = sessionStorage.getItem('pendingRedeemToken');
    if (pendingToken) {
      setTokenCode(pendingToken);
      setShowRedeem(true);
      sessionStorage.removeItem('pendingRedeemToken');
    }
  }, [searchParams, setSearchParams]);

  const loadData = async () => {
    try {
      const [linksRes, instrRes] = await Promise.all([
        linksAPI.getAll(),
        tokenAPI.getInstructions()
      ]);
      setLinks(linksRes.data);
      setInstructions(instrRes.data.instructions || '');
    } catch {
      toast.error('Errore nel caricamento degli OBC');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!tokenCode.trim()) {
      toast.error('Inserisci un codice token');
      return;
    }
    setRedeeming(true);
    try {
      const res = await tokenAPI.redeem(tokenCode.trim());
      toast.success(res.data.message);
      setShowRedeem(false);
      setTokenCode('');
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.startsWith('UPGRADE_REQUIRED:')) {
        toast.error(detail.replace('UPGRADE_REQUIRED:', ''));
        setShowRedeem(false);
        navigate('/subscription');
      } else {
        toast.error(detail || 'Token non valido');
      }
    } finally {
      setRedeeming(false);
    }
  };

  const handleDeleteLink = async () => {
    if (!deleteLink) return;
    try {
      await linksAPI.delete(deleteLink.link_id);
      toast.success('OBC rimosso');
      setDeleteLink(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione OBC');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="my-links-title">
            I Miei OBC
          </h1>
          <p className="text-muted-foreground mt-1">
            Visualizza i tuoi OBC o aggiungine uno tramite token
          </p>
        </div>
        <div className="flex gap-2">
          {instructions && (
            <Button
              variant="outline"
              onClick={() => setShowInstructions(true)}
              data-testid="show-instructions-btn"
            >
              <Info className="w-4 h-4 mr-2" /> Istruzioni
            </Button>
          )}
          <Button
            onClick={() => setShowRedeem(true)}
            data-testid="redeem-token-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Key className="w-4 h-4 mr-2" /> Aggiungi con Token
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : links.length === 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="py-16 text-center">
            <Link2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Manrope' }}>Nessun OBC Disponibile</h3>
            <p className="text-muted-foreground mb-4">
              Aggiungi un OBC inserendo il token fornito dall'amministratore
            </p>
            <Button
              onClick={() => setShowRedeem(true)}
              data-testid="redeem-token-empty-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Key className="w-4 h-4 mr-2" /> Inserisci Token
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {links.map((link) => (
            <Card
              key={link.link_id}
              data-testid={`my-link-${link.link_id}`}
              className="border-border bg-card hover:border-primary/30 transition-all duration-200 group"
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={() => navigate(`/view/${link.link_id}`)}
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Link2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>{link.name}</h3>
                        {lanStatuses[link.link_id]?.reachable && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-500/10 text-green-500 border border-green-500/20" data-testid={`lan-badge-${link.link_id}`}>
                            <Wifi className="w-3 h-3" /> LAN
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Date(link.created_at).toLocaleDateString('it-IT')}
                      </p>
                      {link.redeemed_token && (
                        <span className="text-xs text-primary/70 mt-0.5 block">via token</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => { e.stopPropagation(); setDeleteLink(link); }}
                      data-testid={`delete-link-${link.link_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <ExternalLink
                      className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors cursor-pointer"
                      onClick={() => navigate(`/view/${link.link_id}`)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Redeem Token Dialog */}
      <Dialog open={showRedeem} onOpenChange={setShowRedeem}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Aggiungi OBC con Token</DialogTitle>
            <DialogDescription>
              Inserisci il codice token fornito dall'amministratore per aggiungere un nuovo OBC
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Codice Token</Label>
              <Input
                data-testid="redeem-token-input"
                placeholder="Inserisci il codice token..."
                value={tokenCode}
                onChange={(e) => setTokenCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
                className="font-mono"
              />
            </div>
            {instructions && (
              <div className="p-3 rounded-md bg-secondary/50 border border-border">
                <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                  <Info className="w-3 h-3" /> Come trovare il token:
                </p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="redeem-instructions-preview">
                  {instructions.length > 200 ? instructions.substring(0, 200) + '...' : instructions}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeem(false)}>Annulla</Button>
            <Button
              data-testid="confirm-redeem-token"
              onClick={handleRedeem}
              disabled={redeeming || !tokenCode.trim()}
              className="bg-primary text-primary-foreground"
            >
              <Key className="w-4 h-4 mr-2" />
              {redeeming ? 'Riscatto...' : 'Riscatta Token'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instructions Dialog */}
      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Come trovare il Token</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 rounded-lg bg-secondary/30 border border-border">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed" data-testid="full-instructions-text">
                {instructions}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInstructions(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete OBC Confirmation */}
      <AlertDialog open={!!deleteLink} onOpenChange={() => setDeleteLink(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope' }}>Rimuovi OBC</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler rimuovere il link <strong>{deleteLink?.name}</strong>? Questa azione non puo' essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-my-link"
              onClick={handleDeleteLink}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
